import { useEffect, useState } from 'react'
import { useUser } from 'reactfire'

// The uid of the person actually sitting at the keyboard — the impersonator,
// never the impersonated user.
//
// `impersonate/start` signs the impersonator in AS the target, so `user.uid` is
// the impersonated user's for the whole session. Anything keyed on it becomes
// that person's: their pinned teammates and companies, their sidebar order,
// their preferences. Operators edited a colleague's pin list without realising
// it wasn't theirs (CL-2166), because the rail looked like their own.
//
// The client mirror of the server's `resolveActingUid` (functions chatwoot/api),
// which already resolves the Chatwoot agent, the inbox and every outbound
// message from the impersonator (CL-2069) — so this closes the gap on the
// browser side rather than introducing a new rule.
//
// NOT for anything the security rules check. Storage paths for outbound
// attachments stay on `user.uid`, because the rules and the callable both
// validate the path against the *session* uid.
export function useActingUid(): { actingUid: string | undefined } {
  const { data: user } = useUser()
  const [actingUid, setActingUid] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!user) {
      setActingUid(undefined)
      return
    }
    let cancelled = false
    user
      .getIdTokenResult()
      .then((result) => {
        if (cancelled) return
        const { claims } = result
        if (!claims.impersonating) {
          setActingUid(user.uid)
          return
        }
        // Impersonating, so `user.uid` is the colleague's and is never the
        // answer. A claim that says impersonation is active without naming a
        // usable impersonator leaves us with no identity at all — falling back
        // to the session uid here is exactly the cross-account write this hook
        // exists to prevent.
        const impersonatorUid =
          typeof claims.impersonatorUid === 'string' && claims.impersonatorUid
            ? claims.impersonatorUid
            : undefined
        setActingUid(impersonatorUid)
      })
      .catch(() => {
        if (cancelled) return
        // Without the token we cannot rule out impersonation, and guessing
        // `user.uid` is exactly how a colleague's list gets edited. Stay
        // unidentified instead: consumers show nothing and, more importantly,
        // write nothing. Resolves on the next token read.
        setActingUid(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  return { actingUid }
}
